import { PLAYER_COLORS } from '@rune/shared/constants.js';
import { GAME_MODES, MAP_LIST, mapsForMode } from '@rune/shared/map/maps/index.js';
import { BOT_LEVELS, DEFAULT_BOT_LEVEL } from '@rune/shared/data/bots.js';
import { ROOM_STATUS } from '@rune/shared/protocol.js';
import { h, readStorage, writeStorage } from './dom.js';
import { createChatBox } from './chatBox.js';

export const TEAM_NAMES = ['1팀', '2팀'];
/** 팀 색: 팀 0은 따뜻한 색(슬롯 0), 팀 1은 차가운 색(슬롯 1) */
export const TEAM_COLORS = [PLAYER_COLORS[0], PLAYER_COLORS[1]];

const mapName = (mapId) => MAP_LIST.find((m) => m.id === mapId)?.name ?? mapId;

/** 방장이 마지막으로 고른 봇 난이도: 새로 넣는 봇이 이 난이도로 들어온다 (이 브라우저에만 기억한다) */
const BOT_LEVEL_KEY = 'rune.botLevel';
function savedBotLevel() {
  const level = readStorage(BOT_LEVEL_KEY);
  return level && Object.hasOwn(BOT_LEVELS, level) ? level : DEFAULT_BOT_LEVEL;
}

/**
 * 대기실: 경기 방식·맵(방장만 바꾼다), 팀 두 줄, 준비.
 * 방장은 빈 자리에 AI 봇을 넣고 빼며, 봇마다 난이도를 따로 고른다 (봇은 늘 준비 완료).
 * 팀 인원이 모두 차고 모두 준비하면 서버가 카운트다운을 시작한다.
 */
export function createRoomScreen({
  me,
  onReady,
  onLeave,
  onTeam,
  onSettings,
  onAddBot = () => {},
  onRemoveBot = () => {},
  onBotLevel = () => {},
  getChatMessages = () => [],
  onSendChat,
}) {
  let myReady = false;
  let countdownTimer = null;
  let botLevel = savedBotLevel();
  let chat = null; // 팀전인지에 따라 전체·팀 버튼이 달라서 방 정보를 받은 뒤 만든다
  let chatTeamGame = null;
  const chatSlot = h('div', { class: 'room-chat' });

  const title = h('h1', { class: 'room-title' });
  const hint = h('p', { class: 'note' });
  const settings = h('div', { class: 'room-settings' });
  const teams = h('div', { class: 'teams' });
  const countdown = h('p', { class: 'countdown', 'aria-live': 'assertive' });
  const readyButton = h('button', { class: 'btn btn-primary', type: 'button', onClick: () => onReady(!myReady) }, '준비');
  const leaveButton = h('button', { class: 'btn', type: 'button', onClick: onLeave }, '나가기');

  const el = h(
    'main',
    { class: 'screen' },
    h(
      'div',
      { class: 'room' },
      h('div', { class: 'room-head' }, h('p', { class: 'note' }, '대기실'), title, hint),
      settings,
      teams,
      countdown,
      chatSlot,
      h('div', { class: 'room-actions' }, leaveButton, readyButton),
    ),
  );

  function renderSettings(room) {
    const isHost = room.hostUid === me.uid;
    if (room.ranked) {
      settings.replaceChildren(
        h('span', { class: 'replay-badge' }, '랭킹전'),
        h('span', { class: 'setting-chip' }, GAME_MODES[room.mode].name),
        h('span', { class: 'setting-chip' }, mapName(room.mapId)),
      );
      return;
    }
    if (!isHost) {
      settings.replaceChildren(
        h('span', { class: 'setting-chip' }, GAME_MODES[room.mode].name),
        h('span', { class: 'setting-chip' }, mapName(room.mapId)),
      );
      return;
    }
    const modeSelect = h(
      'select',
      { class: 'input select', 'aria-label': '경기 방식' },
      ...Object.values(GAME_MODES).map((mode) => h('option', { value: mode.id }, mode.name)),
    );
    modeSelect.value = room.mode;
    modeSelect.addEventListener('change', () => onSettings({ mode: modeSelect.value }));
    const mapSelect = h(
      'select',
      { class: 'input select', 'aria-label': '맵' },
      ...mapsForMode(room.mode).map((map) => h('option', { value: map.id }, map.name)),
    );
    mapSelect.value = room.mapId;
    mapSelect.addEventListener('change', () => onSettings({ mapId: mapSelect.value }));
    settings.replaceChildren(
      h('label', { class: 'setting' }, h('span', { class: 'label' }, '방식'), modeSelect),
      h('label', { class: 'setting' }, h('span', { class: 'label' }, '맵'), mapSelect),
    );
  }

  /** 봇 카드의 난이도: 방장은 고르고(대기 중에만), 다른 사람은 보기만 한다 */
  function botLevelControl(room, player, isHost) {
    const level = BOT_LEVELS[player.level] ?? BOT_LEVELS[DEFAULT_BOT_LEVEL];
    if (!isHost) return h('span', { class: 'slot-label' }, `AI · ${level.name}`);
    const select = h(
      'select',
      {
        class: 'input select slot-level',
        'aria-label': `${player.nickname} 난이도`,
        title: level.desc,
        disabled: room.status !== ROOM_STATUS.WAITING,
      },
      ...Object.values(BOT_LEVELS).map((option) => h('option', { value: option.id, title: option.desc }, option.name)),
    );
    select.value = level.id;
    select.addEventListener('change', () => {
      botLevel = select.value; // 다음에 넣는 봇도 이 난이도로
      writeStorage(BOT_LEVEL_KEY, botLevel);
      onBotLevel(player.uid, select.value);
    });
    return h('span', { class: 'slot-head-level' }, h('span', { class: 'slot-label' }, 'AI'), select);
  }

  function playerCard(room, player, team) {
    const style = `--slot-color: ${TEAM_COLORS[team]}`;
    const isHost = room.hostUid === me.uid && !room.ranked;
    if (!player) {
      const canAdd = isHost && room.status === ROOM_STATUS.WAITING;
      return h(
        'div',
        { class: 'slot is-empty', style },
        h('span', { class: 'slot-name' }, '빈 자리'),
        canAdd
          ? h(
              'button',
              { class: 'btn btn-sm slot-add', type: 'button', onClick: () => onAddBot(team, botLevel) },
              'AI 넣기',
            )
          : h('span', { class: 'slot-state' }, '기다리는 중'),
      );
    }
    if (player.bot) {
      const canRemove = isHost && room.status !== ROOM_STATUS.PLAYING;
      return h(
        'div',
        { class: 'slot is-bot', style },
        h(
          'div',
          { class: 'slot-head' },
          botLevelControl(room, player, isHost),
          canRemove
            ? h(
                'button',
                { class: 'btn btn-sm', type: 'button', 'aria-label': `${player.nickname} 빼기`, onClick: () => onRemoveBot(player.uid) },
                '빼기',
              )
            : null,
        ),
        h('span', { class: 'slot-name' }, player.nickname),
        h('span', { class: 'slot-state is-ready' }, '준비 완료'),
      );
    }
    const tags = [];
    if (player.uid === me.uid) tags.push('나');
    if (player.uid === room.hostUid) tags.push('방장');
    return h(
      'div',
      { class: 'slot', style },
      tags.length ? h('span', { class: 'slot-label' }, tags.join(' · ')) : null,
      h('span', { class: 'slot-name' }, player.nickname),
      player.connected === false
        ? h('span', { class: 'slot-state' }, '연결 끊김 · 재접속 기다리는 중')
        : h('span', { class: player.ready ? 'slot-state is-ready' : 'slot-state' }, player.ready ? '준비 완료' : '준비 중'),
    );
  }

  function renderTeams(room) {
    const size = GAME_MODES[room.mode].teamSize;
    const mine = room.players.find((p) => p.uid === me.uid);
    teams.replaceChildren(
      ...[0, 1].map((team) => {
        const members = room.players.filter((p) => p.team === team);
        const cards = Array.from({ length: size }, (_, i) => playerCard(room, members[i], team));
        const canJoin = mine && mine.team !== team && members.length < size && !room.ranked;
        return h(
          'section',
          { class: 'team', style: `--team-color: ${TEAM_COLORS[team]}`, 'aria-label': TEAM_NAMES[team] },
          h(
            'header',
            { class: 'team-head' },
            h('span', { class: 'team-name' }, TEAM_NAMES[team]),
            h('span', { class: 'team-count mono' }, `${members.length}/${size}`),
            canJoin ? h('button', { class: 'btn btn-sm', type: 'button', onClick: () => onTeam(team) }, '이 팀으로') : null,
          ),
          h('div', { class: 'team-slots' }, ...cards),
        );
      }),
    );
  }

  function update(room) {
    const teamGame = room.mode !== '1v1';
    if (chatTeamGame !== teamGame) {
      chat?.destroy();
      chat = createChatBox({ messages: getChatMessages(), teamGame, variant: 'panel', onSend: onSendChat });
      chatTeamGame = teamGame;
      chatSlot.replaceChildren(chat.el);
    }
    title.textContent = room.name;
    renderSettings(room);
    renderTeams(room);

    myReady = Boolean(room.players.find((p) => p.uid === me.uid)?.ready);
    readyButton.textContent = myReady ? '준비 취소' : '준비';
    readyButton.classList.toggle('btn-primary', !myReady);
    readyButton.hidden = room.ranked; // 랭킹전은 자동으로 시작한다

    const empty = room.maxPlayers - room.players.length;
    const isHost = room.hostUid === me.uid;
    const practice = room.players.some((p) => p.bot) ? ' AI 봇이 있는 경기는 연습 경기라 전적에 남지 않습니다.' : '';
    hint.textContent = room.ranked
      ? '매칭된 상대와 곧 시작합니다. 지금 나가면 매칭이 취소됩니다.'
      : (empty > 0
          ? isHost
            ? `${empty}자리가 비어 있습니다. 사람을 기다리거나 빈 자리에 AI 봇을 넣으세요.`
            : `${empty}자리가 더 차면 모두 준비를 눌러 시작합니다.`
          : room.players.every((p) => p.ready)
            ? '모두 준비했습니다. 곧 시작합니다.'
            : '모두 준비하면 게임이 시작됩니다.') + practice;
  }

  function setCountdown(seconds) {
    clearInterval(countdownTimer);
    if (!seconds) {
      countdown.textContent = '';
      return;
    }
    let left = Math.ceil(seconds);
    countdown.textContent = String(left);
    countdownTimer = setInterval(() => {
      left -= 1;
      countdown.textContent = left > 0 ? String(left) : '';
      if (left <= 0) clearInterval(countdownTimer);
    }, 1000);
  }

  return {
    el,
    update,
    setCountdown,
    addChatMessage: (message) => chat?.add(message),
    resetChat: (messages) => chat?.reset(messages),
    destroy: () => {
      clearInterval(countdownTimer);
      chat?.destroy();
    },
  };
}
